import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AlertController, ModalController, PopoverController, ToastController } from '@ionic/angular/standalone';

@Injectable({
  providedIn: 'root',
})
export class ComponentsService {
  popover: any;
  isAlertLoaded: boolean = false;

  constructor(
    public popoverController: PopoverController,
    private alertController: AlertController,
    private toastController: ToastController,
    public modalController: ModalController,
    private router: Router
  ) { }

  /**
   * This function creates a toast message with the message and color passed in as parameters
   * @param {string} message - The message you want to display in the toast.
   * @param {string} color - The color of the toast.
   */
  async presentToast(message: string, color?: string) {
    const toast = await this.toastController.create({
      message: message,
      position: 'bottom',
      color: color ?? 'dark',
      duration: 2000,
    });
    toast.present();
  }

  /**
   * It creates a toast notification with a title, message, color, and optional url. If a url is
   * provided, it will add a button to the toast that will navigate to the url when clicked
   * @param {string} title - The title of the notification
   * @param {string} message - The message to be displayed in the toast.
   * @param {string} color - The color of the toast.
   * @param {string} [url] - The url to navigate to when the user clicks the "VIEW" button.
   */
  async presentNotificationToast(
    title: string,
    message: string,
    color: string,
    url?: string
  ) {
    const toast = await this.toastController.create({
      header: title,
      message: message,
      position: 'top',
      color: color,
      icon: 'notifications',
      duration: 5000,
    });
    toast.present();
  }

  /**
   * It creates a modal, sets the component to the page passed in, sets the componentProps to the props
   * passed in, and then presents the modal
   * @param {any} page - The page you want to open in the modal.
   * @param {any} props - any - this is the data that you want to pass to the modal.
   */
  async presentModal(page: any, props: any) {
    const modal = await this.modalController.create({
      component: page,
      mode: 'ios',
      cssClass: 'my-modal-class',
      componentProps: props,
      canDismiss: true,
    });
    await modal.present();

    const { data } = await modal.onWillDismiss();
  }

  async presentConfirmAlert(
    header: string,
    subHeader: string,
    message: string,
    action: { actionText: string; cancelText?: string }
  ) {
    if (this.popover || this.isAlertLoaded) return;

    const buttonArray = [
      ...(action.cancelText ? [{ text: action.cancelText }] : []),
      {
        text: action.actionText,
        role: 'confirm',
      },
    ];

    this.popover = await this.alertController.create({
      header,
      subHeader,
      message,
      buttons: buttonArray,
    });
    await this.popover.present();

    const { role } = await this.popover.onDidDismiss();

    this.isAlertLoaded = false;
    if (role || role == undefined) this.popover = null;
    return role && role != 'backdrop' ? role : false;
  }

  async openModal(
    component: any,
    roleToCheck: string,
    callback: (data: any) => void,
    componentProps?: any,
    initialBreakpoint: number = 0.7
  ) {
    const modal = await this.modalController.create({
      component: component,
      breakpoints: [0, 0.7, 0.85, 1],
      cssClass: 'picker-modal',
      handle: true,
      handleBehavior: 'cycle',
      initialBreakpoint: initialBreakpoint,
      componentProps: componentProps,
      mode: 'md',
    });
    modal.present();

    const { data, role } = await modal.onWillDismiss();

    if (role === roleToCheck) {
      callback(data);
    }
  }

  /**
   * It takes a path as a parameter, and then navigates to that path
   * @param {any} path - The path to navigate to.
   */
  async navigateTo(path: any) {
    await this.router.navigate([path]);
  }

  /**
   * It creates a modal, passes in the component and componentProps, presents the modal, and then waits
   * for the modal to be dismissed
   * @param props - This is the data that you want to pass to the modal.
   * @returns The data object is being returned.
   */
  async viewMedia(page: any, props: any) {
    const modal = await this.modalController.create({
      component: page,
      componentProps: props,
    });

    await modal.present();

    const { data } = await modal.onWillDismiss();

    if (data) {
    }
  }

  async presentPopover(e: Event, component: any) {
    if (this.popover) return;

    this.popover = await this.popoverController.create({
      component,
      event: e,
      dismissOnSelect: true,
    });

    await this.popover.present();

    const { role } = await this.popover.onDidDismiss();

    if (role || role == undefined) this.popover = null;
  }

}
